import axios, { type AxiosError } from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { SESSION_STORAGE_CHAVE_USER } from '@/config/constantes'

import keycloak, {
  inicializarKeycloak,
  limparRotaPosLogin,
  login,
  logout,
  obterRotaPosLogin,
  ROTA_PADRAO_POS_LOGIN,
  salvarRotaPosLogin,
} from '@/config/keycloak'

import type { Usuario } from '@/models/usuario'
import { servicoBase } from '@/services/servico-base'
import { verificarSeAceitouTermos } from '@/services/termos/buscar-termos'
import { buscarEu } from '@/services/usuarios/buscar-eu'

import type { AuthContextData } from './types'

export function useBloc() {
  const navigate = useNavigate()
  const location = useLocation()

  const [carregandoSistema, setCarregandoSistema] = useState<boolean>(true)

  const [usuarioLogado, setUsuarioLogado] = useState<Partial<Usuario> | null>(
    () => {
      const user = sessionStorage.getItem(SESSION_STORAGE_CHAVE_USER)
      return user ? JSON.parse(user) : null
    }
  )

  const atualizarDadosUsuarioLogado = useCallback((dados: Partial<Usuario>) => {
    sessionStorage.setItem(SESSION_STORAGE_CHAVE_USER, JSON.stringify(dados))
    setUsuarioLogado(dados)
  }, [])

  const limparLocalStorage = useCallback(() => {
    sessionStorage.removeItem(SESSION_STORAGE_CHAVE_USER)
    servicoBase.defaults.headers.common.Authorization = ''
  }, [])

  const deslogar = useCallback(
    async (urlDeRedirecionamento?: string): Promise<void> => {
      limparLocalStorage()
      limparRotaPosLogin()

      await logout(urlDeRedirecionamento)
    },
    []
  )

  const verificarTermosDeUso = useCallback(async (): Promise<void> => {
    const { url, aceito } = await verificarSeAceitouTermos(
      `${window.location.origin}${window.location.pathname}${window.location.search}`
    )

    if (!aceito) window.open(url, '_self')
  }, [])

  const verificarSeEstaLogado = useCallback(async () => {
    if (location.pathname.includes('/publico')) {
      setCarregandoSistema(false)
      return
    }

    const { authenticated } = keycloak

    setCarregandoSistema(false)

    if (!authenticated && location.pathname !== '/') {
      salvarRotaPosLogin(`${location.pathname}${location.search}`)
      limparLocalStorage()
      navigate('/')
      return
    }

    if (authenticated) {
      const rotaPosLogin = obterRotaPosLogin()
      limparRotaPosLogin()

      if (location.pathname === '/') {
        navigate(rotaPosLogin ?? ROTA_PADRAO_POS_LOGIN)
        return
      }

      await verificarTermosDeUso()
    }
  }, [location.pathname, location.search, navigate])

  const carregarInformacoesDoUsuario =
    useCallback(async (): Promise<boolean> => {
      const resposta = await buscarEu()

      if (axios.isAxiosError(resposta)) {
        const error = resposta as AxiosError<{ detail?: string }>

        const mensagem = error.message.includes('Network Error')
          ? 'Servidor indisponível no momento. Tente novamente mais tarde.'
          : error.response?.data?.detail ||
            'Erro ao carregar informações do usuário'

        sessionStorage.setItem(
          'erro-generico',
          JSON.stringify({
            titulo: 'Falha ao tentar se autenticar',
            texto: mensagem,
            tipo: 'erro',
          })
        )

        await deslogar()
        return false
      }

      const usuario = resposta as Partial<Usuario>

      atualizarDadosUsuarioLogado(usuario)

      return true
    }, [verificarSeAceitouTermos])

  const atualizarTokenNaAplicacao = useCallback(async (token: string) => {
    servicoBase.defaults.headers.common.Authorization = `Bearer ${token}`
  }, [])

  const aoEfetuarLoginComSucesso = useCallback(async (): Promise<boolean> => {
    atualizarTokenNaAplicacao(keycloak.token as string)

    return carregarInformacoesDoUsuario()
  }, [verificarSeAceitouTermos])

  const estadoAutenticacao = useMemo<AuthContextData>((): AuthContextData => {
    return {
      ...(usuarioLogado ?? {}),
      token: keycloak.token,
      carregandoSistema,
      estaAutenticado: keycloak.authenticated,
      logar: login,
      deslogar,
      atualizarDadosUsuarioLogado,
    } as AuthContextData
  }, [usuarioLogado, carregandoSistema])

  const inicializarAutenticacao = useCallback(async () => {
    if (!keycloak.didInitialize) await inicializarKeycloak()
    if (keycloak.authenticated) {
      const usuarioCarregado = await aoEfetuarLoginComSucesso()
      if (!usuarioCarregado) return
    }

    await verificarSeEstaLogado()
  }, [aoEfetuarLoginComSucesso, verificarSeEstaLogado])

  useEffect(() => {
    const loadingDiv = document.getElementById('pre-loading') as HTMLDivElement

    if (!carregandoSistema) loadingDiv.setAttribute('style', 'display: none;')
  }, [carregandoSistema])

  useEffect(() => {
    inicializarAutenticacao()
  }, [])

  return estadoAutenticacao
}
